/* The frame every product app sits in (D46: the shell owns nav, search,
 * notifications and theme; the modules own the screens).
 *
 * It is deliberately dumb about what is in it. Navigation is data handed in
 * by the app, so adding a module is adding entries -- never editing the shell.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { applyTheme, readTheme } from './theme'
import type { Theme } from './theme'

export type NavItem = { label: string; to: string; badge?: number | string }
export type NavGroup = { title: string; items: NavItem[] }

type Props = {
  product: string
  nav: NavGroup[]
  current: string
  onNavigate: (to: string) => void
  onSearch?: () => void
  who?: ReactNode
  /** Goes in the top bar, on the right: the notification bell and anything
   *  else that has to be reachable from every screen.
   *
   *  It exists because the bell used to live in `who`, at the foot of the
   *  side navigation — which is `overflow: hidden` and a full `100dvh` tall,
   *  so the panel opened downwards from the very bottom of the window and was
   *  clipped away entirely. It looked like a bell that did nothing. */
  actions?: ReactNode
  children: ReactNode
}

export function AppShell({ product, nav, current, onNavigate, onSearch, who, actions, children }: Props) {
  const [open, setOpen] = useState(false)
  const [theme, setTheme] = useState<Theme>(() => readTheme())

  useEffect(() => { applyTheme(theme) }, [theme])
  // Route changes close the drawer: leaving it open over the new screen is
  // the classic phone-navigation bug.
  useEffect(() => { setOpen(false) }, [current])

  // Escape closes it. The open drawer covers the button that opened it, and
  // the sliver of scrim left beside it on a 390px screen is about 55px --
  // reachable, but not something to rely on as the only way out.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <div className="shell">
      {/* WCAG 2.4.1. There are around fifty links in the menu, so without
          this a keyboard user presses Tab fifty times to reach the table
          they came for — measured, and it is exactly what happened. */}
      <a className="skip" href="#content"
         onClick={(e) => {
           e.preventDefault()
           const main = document.getElementById('content')
           main?.focus()
           main?.scrollIntoView()
         }}>Skip to the content</a>
      {/* A mouse convenience, not a control: the drawer has its own Close
          button and Escape closes it too. Giving this the same accessible
          name meant two controls called "Close menu", which is confusing to a
          screen reader and ambiguous to anything else reading the page. */}
      {open ? <div className="scrim" aria-hidden="true" onClick={() => setOpen(false)} /> : null}
      <nav className="shell-nav" data-open={open} aria-label="Sections">
        <div className="row" style={{ padding: '14px 16px', gap: 10 }}>
          <Mark />
          <strong style={{ fontSize: 15, letterSpacing: '-0.01em' }}>{product}</strong>
          <span className="grow" />
          <button className="btn btn-quiet btn-sm narrow-only" aria-label="Close menu"
                  onClick={() => setOpen(false)}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 16 }}>
          {nav.map((group) => (
            <div className="navgroup" key={group.title}>
              <div className="navgroup-title">{group.title}</div>
              {group.items.map((item) => (
                <a
                  key={item.to}
                  className="navitem"
                  href={item.to}
                  aria-current={item.to === current ? 'page' : undefined}
                  onClick={(e) => { e.preventDefault(); onNavigate(item.to) }}
                >
                  <span className="grow truncate">{item.label}</span>
                  {item.badge ? <span className="chip chip-info">{item.badge}</span> : null}
                </a>
              ))}
            </div>
          ))}
        </div>
        <div className="row" style={{ padding: 12, borderTop: '1px solid var(--line)', gap: 6 }}>
          <ThemeToggle theme={theme} onChange={setTheme} />
          <div className="grow" />
          {who}
        </div>
      </nav>

      <div className="shell-main">
        <header className="topbar">
          {/* The only way to reach the menu on a phone, where the drawer is
              off-screen. It was styled `display:none` outright at first,
              which hid it everywhere and left a phone with no navigation at
              all -- caught by a 390px screenshot. */}
          <button className="btn btn-quiet btn-sm narrow-only" onClick={() => setOpen((v) => !v)}
                  aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open}>
            <Burger />
          </button>
          <button className="btn btn-quiet" style={{ justifyContent: 'flex-start', color: 'var(--ink-3)', flex: '1 1 auto', maxWidth: 420 }}
                  onClick={onSearch}>
            <Glass /> <span className="grow" style={{ textAlign: 'left' }}>Search anything</span>
            <span className="kbd">Ctrl K</span>
          </button>
          <span style={{ flex: '1 1 0', minWidth: 0 }} />
          {actions}
        </header>
        <main className="shell-body" id="content" tabIndex={-1}>{children}</main>
      </div>
    </div>
  )
}

function ThemeToggle({ theme, onChange }: { theme: Theme; onChange: (t: Theme) => void }) {
  const next: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' }
  const label: Record<Theme, string> = { system: 'Match device', light: 'Light', dark: 'Dark' }
  return (
    <button className="btn btn-quiet btn-sm" onClick={() => onChange(next[theme])}
            title={`Theme: ${label[theme]}`} aria-label={`Theme: ${label[theme]}. Change.`}>
      {theme === 'dark' ? <Moon /> : theme === 'light' ? <Sun /> : <Auto />}
    </button>
  )
}

/** Ctrl/Cmd-K anywhere, without stealing it from a text field the person is
 *  typing in. */
export function useCommandKey(open: () => void) {
  const latest = useRef(open)
  latest.current = open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        latest.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

const Mark = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="6" fill="var(--accent)" />
    <path d="M8 8h8v6.5A3.5 3.5 0 0 1 12.5 18H11" stroke="var(--accent-ink)" strokeWidth="2.1" fill="none" strokeLinecap="round" />
  </svg>
)
const Glass = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5 14 14" strokeLinecap="round" />
  </svg>
)
const Burger = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M2 4h12M2 8h12M2 12h12" strokeLinecap="round" />
  </svg>
)
const Sun = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="8" cy="8" r="3" /><path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3 3l1 1M12 12l1 1M13 3l-1 1M4 12l-1 1" strokeLinecap="round" />
  </svg>
)
const Moon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5Z" />
  </svg>
)
const Auto = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="8" cy="8" r="6" /><path d="M8 2v12" /><path d="M8 2a6 6 0 0 1 0 12" fill="currentColor" stroke="none" />
  </svg>
)
